import { useMutation } from '@apollo/client'
import { Drawer, Input, message, Radio } from 'antd'
import classNames from 'classnames'
import { useAtom } from 'jotai'
import { useRouter } from 'next/router'
import { memo, useEffect, useRef, useState } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { MyForm } from '~/components/ui/MyForm'
import { MyIcon } from '~/components/ui/MyIcon'
import { MyModal } from '~/components/ui/MyModal'
import { CREATE_DANLU } from '~/graphqls/gqls/danlu'
import {
  createDanlu,
  createDanluVariables,
  getGpuList_itemModule_productItemPage_edges_node_ProductItem,
} from '~/graphqls/types'
import { graphQLErrorMessage } from '~/utils'
import styles from './CreateDrawer.module.css'
import GpuCard from './GpuCard'
import { scheduleListUpdateAtom } from './useAtom'

interface Props {
  visible: boolean
  onClose: () => void
  gpuList: getGpuList_itemModule_productItemPage_edges_node_ProductItem[]
}

function CreateDrawer({ visible, onClose, gpuList }: Props) {
  const [form] = MyForm.useForm()
  const selectedImage = MyForm.useWatch('image', form)
  const [selectedGpu, setSelectedGpu] = useState(-1)
  // result modal
  const [resultModalVisible, setResultModalVisible] = useState(false)
  const [resultSuccess, setResultSuccess] = useState(true)
  const [resultErrMsg, setResultErrMsg] = useState('')
  const [, scheduleListUpdate] = useAtom(scheduleListUpdateAtom)
  const submitLock = useRef(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const router = useRouter()

  const [createDanluAction] = useMutation<createDanlu, createDanluVariables>(
    CREATE_DANLU
  )

  const firstGpuId = gpuList[0]?.id
  useEffect(() => {
    if (firstGpuId && selectedGpu === -1) {
      setSelectedGpu(firstGpuId)
    }
  }, [firstGpuId])

  const selectedGpuInfo = gpuList.find(item => {
    return item.id === selectedGpu
  })

  const handleSubmit = async () => {
    if (!selectedGpu || selectedGpu === -1) {
      message.destroy()
      message.error('请选择GPU')
      return
    }
    let formData
    try {
      formData = await form.validateFields()
    } catch (err: any) {
      throw err
    }
    try {
      if (submitLock.current) return
      submitLock.current = true
      setSubmitLoading(true)
      const res = await createDanluAction({
        variables: {
          input: {
            gpuTypeId: selectedGpu,
            name: formData.name,
            // 目前镜像type固定1
            snapshotType: 1,
          },
        },
      })
      scheduleListUpdate(true)
      // setResultModalVisible(true)
      // setResultSuccess(true)
      onClose()
      if (router.pathname !== '/pro/danlu') {
        router.push('/pro/danlu')
      }
    } catch (err) {
      const errMsg = graphQLErrorMessage(err) || '未知错误'
      setResultModalVisible(true)
      setResultSuccess(false)
      setResultErrMsg(errMsg)
    } finally {
      submitLock.current = false
      setSubmitLoading(false)
    }
  }

  return (
    <Drawer
      width={584}
      open={visible}
      onClose={onClose}
      closable={false}
      bodyStyle={{ padding: 0 }}
    >
      <section className="w-full h-full relative pb-[80px] pt-4 px-6 flex-shrink-0">
        <div className="flex items-center text-xl justify-between text-neutral-800 mb-6">
          <span>新建丹炉</span>
          <span
            className="cursor-pointer w-10 h-10 rounded-full border-2 border-solid border-neutral-300 hover:border-neutral-400 flex items-center transition-all"
            onClick={onClose}
          >
            <MyIcon
              icon="close"
              className="text-neutral-400 mx-auto hover:text-neutral-500 transition-all"
            />
          </span>
        </div>
        <div
          className="overflow-y-auto scrollbar-hidden"
          style={{ height: 'calc(100vh - 148px)' }}
        >
          <MyForm
            layout="vertical"
            $dark
            form={form}
            initialValues={{
              image: 1,
            }}
          >
            <div className="mb-6">
              <div className={styles.label}>丹炉名称</div>
              <MyForm.Item
                name="name"
                rules={[
                  { required: true, message: '请输入丹炉名称' },
                  { validateTrigger: ['onChange'] },
                  { min: 3, message: '丹炉名称需在3-13个字符内' },
                ]}
                normalize={v => v.trim()}
              >
                <Input placeholder="请输入丹炉名称" showCount maxLength={13} />
              </MyForm.Item>
            </div>

            <div className="mb-6">
              <div className={styles.label}>选择镜像</div>
              <div className={styles.card}>
                <MyForm.Item name="image" rules={[{ required: true }]} noStyle>
                  <Radio.Group>
                    <div className="flex flex-col">
                      <Radio value={1} className={styles.customRadio}>
                        <div className="flex items-center">
                          <div
                            className={classNames(
                              'w-4 h-4 border-4 border-solid transition-all rounded-full mr-2',
                              {
                                'border-primary': selectedImage === 1,
                                'border-neutral-500': selectedImage !== 1,
                              }
                            )}
                          ></div>
                          <div className="text-neutral-500 text-base font-semibold">
                            Stable Diffusion Web UI
                          </div>
                        </div>
                      </Radio>
                    </div>
                  </Radio.Group>
                </MyForm.Item>
                <div className="text-neutral-500 text-xs">
                  预装：Ubuntu20.04, Python 3.9, Pytorch1.12.1, CUDA 11.3, cuDNN
                  8, NVCC, VNC, Stable Diffusion web UI
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className={styles.label}>选择GPU</div>
              <div className="flex flex-wrap gap-3">
                {gpuList.map((i, index) => {
                  return (
                    <div
                      key={i.id}
                      className={classNames(
                        'rounded-2xl border-2 border-solid w-[260px] cursor-pointer',
                        selectedGpu === i.id
                          ? 'border-primary'
                          : 'border-neutral-300'
                      )}
                      onClick={() => {
                        setSelectedGpu(i.id)
                      }}
                    >
                      <GpuCard data={i} />
                    </div>
                  )
                })}
              </div>
            </div>
          </MyForm>
        </div>

        <div
          className="bg-neutral-100 flex items-center justify-between absolute bottom-0 left-0 right-0 py-2.5 px-6"
          style={{
            boxShadow:
              '0px 1px 4px rgba(255, 255, 255, 0.08), 0px 0px 16px rgba(255, 255, 255, 0.08)',
          }}
        >
          <div className="flex items-center">
            <div className="text-neutral-500">
              <div className="text-[14px] leading-6 font-semibold">镜像：</div>
              <div className="text-sm">Stable Diffusion Web UI</div>
            </div>
            <div className="text-neutral-500 ml-4">
              <div className="text-[14px] leading-6 font-semibold">GPU：</div>
              {/* 用不可见字符，防止布局被破坏 */}
              <div className="text-sm">{selectedGpuInfo?.name || '\u2000'}</div>
            </div>
          </div>
          <div className="flex items-center">
            <MyBtn
              className="!px-10 !border-neutral-200 !bg-neutral-200 !text-neutral-800"
              onClick={onClose}
            >
              取消
            </MyBtn>
            <MyBtn
              type="primary"
              className="!px-10 ml-3"
              onClick={handleSubmit}
              loading={submitLoading}
            >
              确认
            </MyBtn>
          </div>
        </div>
      </section>

      <MyModal
        title={resultSuccess ? '新建成功' : '新建失败'}
        open={resultModalVisible}
        onCancel={() => {
          setResultModalVisible(false)
          onClose()
        }}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        width={448}
        footer={null}
        dark
      >
        {resultSuccess && (
          <div className="text-center">
            <MyEmpty
              image="/images/pro/success.svg"
              description="新建成功"
              className="!mt-[16px] !mb-2"
            />
            <MyBtn className="w-full mt-8" type="primary" size="large">
              查看丹炉
            </MyBtn>
          </div>
        )}
        {!resultSuccess && (
          <div className="text-center">
            <MyEmpty
              image="/images/pro/fail.svg"
              description={resultErrMsg}
              className="!mt-[16px] !mb-2"
            />
            <MyBtn
              className="w-full mt-8"
              type="primary"
              size="large"
              onClick={() => {
                setResultModalVisible(false)
              }}
            >
              再试一次
            </MyBtn>
          </div>
        )}
      </MyModal>
    </Drawer>
  )
}

export default memo(CreateDrawer, (prev, next) => {
  if (prev.visible !== next.visible) {
    return false
  }
  return true
})
